/**
 * SPDX-FileCopyrightText: 2025-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { addStyleEditorA11yOverrideRules } from './a11yCustomStyles.js';

/**
 * Look for the style generated by the Authoring's "Style Editor"
 * Create new rules to adapt this style to our item-runner.
 * Return these new rules.
 * @param {CSSRuleList} cssRules
 * @returns {CSSRuleList?}
 */
export function applyStyleEditorTheme(cssRules) {
    if (!cssRules) {
        return void 0;
    }

    const itemRunnerVarsScope = '.qti-item';
    const derivedRules = [];
    const a11yOverrideScopes = [];

    for (const rule of Object.values(cssRules)) {
        if (!rule.selectorText || !rule.style || !hasStyleEditorProperties(rule.style)) {
            continue;
        }

        const newProps = getStyleEditorThemeProperties(rule.style);
        if (!newProps.length) {
            continue;
        }

        const styleEditorVarsScope = anchorToItemRunnerScope(rule.selectorText, itemRunnerVarsScope);
        derivedRules.push(`${styleEditorVarsScope} {${newProps.join(';')}}`);
        a11yOverrideScopes.push(styleEditorVarsScope);
    }

    if (!derivedRules.length) {
        return void 0;
    }

    const newStylesheet = new CSSStyleSheet();

    a11yOverrideScopes.forEach(styleEditorVarsScope =>
        addStyleEditorA11yOverrideRules(newStylesheet, styleEditorVarsScope)
    );
    derivedRules.forEach(cssText => newStylesheet.insertRule(cssText));

    for (const existingRule of cssRules) {
        try {
            // rules like @import now throw
            newStylesheet.insertRule(existingRule.cssText);
        } catch (err) {
            console.error(err);
        }
    }

    return newStylesheet.cssRules;
}

/**
 * @param {CSSStyleDeclaration} style
 * @returns {boolean}
 */
function hasStyleEditorProperties(style) {
    for (let i = 0; i < style.length; i++) {
        if (style[i].startsWith('--styleeditor-')) {
            return true;
        }
    }
    return false;
}

/**
 * Prefix nested StyleEditor scopes with the item scope. This lets the later
 * stylesheet scoping pass replace `.qti-item` with a passage-specific scope.
 * @param {String} selectorText
 * @param {String} itemRunnerVarsScope
 * @returns {String}
 */
function anchorToItemRunnerScope(selectorText, itemRunnerVarsScope) {
    return selectorText
        .split(/\s*,\s*/)
        .map(selector => {
            if (selector.includes(itemRunnerVarsScope) || selector.includes('body div.qti-item')) {
                return selector;
            }
            return `${itemRunnerVarsScope} ${selector}`;
        })
        .join(', ');
}

/**
 * @param {CSSStyleDeclaration} style
 * @returns {String[]}
 */
function getStyleEditorThemeProperties(style) {
    const newProps = [];

    for (let i = 0; i < style.length; i++) {
        const propertyName = style[i];
        const propertyValue = `var(${propertyName})`;
        const resolvedPropertyValue = style.getPropertyValue(propertyName);

        switch (propertyName) {
            case '--styleeditor-font-size': {
                newProps.push(`--fontsize-body: ${propertyValue}`);
                newProps.push(`--fontsize-body-xs: calc(${propertyValue} * 0.75)`);
                newProps.push(`--fontsize-body-s: calc(${propertyValue} * 0.875)`);
                newProps.push(`--fontsize-heading: ${propertyValue}`);
                newProps.push(`--fontsize-heading-l: calc(${propertyValue} * 1.25)`);
                newProps.push(`--fontsize-heading-xl: calc(${propertyValue} * 1.875)`);
                newProps.push(`--fontsize-heading-xxl: calc(${propertyValue} * 2.5)`);

                newProps.push(`--styleeditor-font-size-initial: ${resolvedPropertyValue}`);
                break;
            }
            case '--styleeditor-font-family': {
                newProps.push(`--font-ui: ${propertyValue}`);
                break;
            }
            case '--styleeditor-bg-color': {
                newProps.push(`--color-bg-default: ${propertyValue}`);
                newProps.push(`--color-bg-input-default: ${propertyValue}`);
                newProps.push(`--color-bg-actionable-secondary: ${propertyValue}`);
                break;
            }
            case '--styleeditor-text-color': {
                newProps.push(`--color-text-default: ${propertyValue}`);
                newProps.push(`--color-text-feedback: ${propertyValue}`);
                newProps.push(`--color-link-primary: ${propertyValue}`);
                break;
            }
            case '--styleeditor-border-color': {
                newProps.push(`--color-border-default: ${propertyValue}`);
                newProps.push(`--color-border-input-default: ${propertyValue}`);
                newProps.push(`--color-border-info: ${propertyValue}`);
                break;
            }
            case '--styleeditor-table-heading-bg-color': {
                newProps.push(`--color-bg-table-heading: ${propertyValue}`);
                break;
            }
            default:
                break;
        }
    }

    return newProps;
}

